import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Table, Tag, Button, message, Tooltip, Alert, Switch, Space } from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import { GlobalOutlined } from '@ant-design/icons';
import { buyerService } from '../../services/api';
import { urlMicrositio } from '../../utils/urlMicrositio';
import dayjs from 'dayjs';

const Remates = () => {
  const [loading, setLoading] = useState(true);
  const [remates, setRemates] = useState([]);
  const [solicitando, setSolicitando] = useState({});
  // Por defecto sólo los remates vigentes (2026-11-04): el listado traía
  // todos los eventos de la base, terminados incluidos, y se podía pedir
  // acceso por un remate que ya pasó. Los finalizados quedan a un switch.
  const [verFinalizados, setVerFinalizados] = useState(false);
  const [searchParams] = useSearchParams();

  // Remate del que venía la persona. Sólo se RESALTA: la solicitud la manda
  // ella con el botón. Un slug viejo autocompletado por el navegador no debe
  // generar pedidos de acceso a cabañas que nadie pidió.
  const eventoSlug = searchParams.get('evento');
  const destacado = eventoSlug
    ? remates.find(r => r.slug === eventoSlug)
    : null;

  useEffect(() => {
    fetchRemates(verFinalizados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verFinalizados]);

  const fetchRemates = async (todos) => {
    setLoading(true);
    try {
      // Si la persona llega por link de un remate (`?evento=`), se pide la
      // lista completa: si ese remate ya terminó tiene que ver el aviso de
      // que terminó, no una lista donde no está.
      const res = await buyerService.getRemates(todos || eventoSlug ? { todos: 1 } : undefined);
      const lista = res.data || [];
      // Con el switch apagado, de la lista completa se muestran los vigentes
      // y el destacado aunque esté finalizado.
      setRemates((todos || !eventoSlug)
        ? lista
        : lista.filter(r => !r.es_finalizado || r.slug === eventoSlug));
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar remates');
    } finally {
      setLoading(false);
    }
  };

  const solicitarAcceso = async (r) => {
    setSolicitando(prev => ({ ...prev, [r.id_clientes]: true }));
    try {
      await buyerService.solicitarAutorizacion(r.id_clientes, r.id_evento);
      message.success('Solicitud enviada. La cabaña la revisará pronto.');
      await fetchRemates(verFinalizados);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al enviar solicitud');
    } finally {
      setSolicitando(prev => ({ ...prev, [r.id_clientes]: false }));
    }
  };

  const columnas = [
    {
      title: 'Cabaña',
      dataIndex: ['cabaña', 'razon_social'],
      key: 'cabana',
      render: (val, r) => r.cabaña?.razon_social || `Cabaña #${r.id_clientes}`,
    },
    {
      title: 'Nombre del evento',
      dataIndex: 'nombre_evento',
      key: 'nombre_evento',
    },
    {
      title: 'Fecha inicio',
      dataIndex: 'fecha_inicio',
      key: 'fecha_inicio',
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Estado',
      key: 'estado',
      // `es_finalizado` lo decide el backend (cerrado a mano o fecha de fin
      // pasada), que es la misma regla de las pre-ofertas y del en vivo.
      render: (_, r) => (r.es_finalizado
        ? <Tag color="red">Finalizado</Tag>
        : <Tag color="green">Activo</Tag>),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      width: 80,
      render: (_, r) =>
        r.slug ? (
          <Tooltip title="Ver micrositio">
            <Button
              type="text"
              icon={<GlobalOutlined />}
              // Respeta el micrositio incrustado: si el productor lo publica en
              // su propio dominio, el comprador tiene que ir ahí y no a la ruta
              // de PUJA, que lo saca del sitio que reconoce.
              href={urlMicrositio({ config: r, eventoSlug: r.slug })}
              target="_blank"
              rel="noopener noreferrer"
            />
          </Tooltip>
        ) : null,
    },
    {
      title: 'Acceso',
      key: 'acceso',
      render: (_, r) => {
        if (r.autorizado) {
          return <Tag color="green">Autorizado</Tag>;
        }
        if (r.solicitud_estado === 'pendiente') {
          return (
            <Button size="small" disabled>
              Solicitud enviada
            </Button>
          );
        }
        if (r.es_finalizado) {
          // Un remate que terminó no abre solicitudes: el backend también lo
          // rechaza, esto evita el botón que lleva a un error.
          return <Tag>Remate finalizado</Tag>;
        }
        return (
          <Button
            size="small"
            type="primary"
            loading={!!solicitando[r.id_clientes]}
            onClick={() => solicitarAcceso(r)}
          >
            Solicitar acceso
          </Button>
        );
      },
    },
  ];

  if (loading && remates.length === 0) {
    return <PujaSpinner />;
  }

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 24 }} wrap>
        <h2 style={{ margin: 0 }}>Remates</h2>
        <Space>
          <span style={{ color: '#666' }}>Mostrar finalizados</span>
          <Switch size="small" checked={verFinalizados} onChange={setVerFinalizados} />
        </Space>
      </Space>

      {destacado && !destacado.autorizado && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Venías del remate "${destacado.nombre_evento}"`}
          description={
            destacado.es_finalizado
              ? 'Ese remate ya finalizó. Cuando la cabaña publique el próximo vas a poder pedir acceso desde esta lista.'
              : destacado.solicitud_estado === 'pendiente'
                ? 'Ya pediste acceso a esa cabaña. Falta que lo aprueben.'
                // Sin "acá abajo": con pageSize 20 la fila resaltada puede estar
                // en otra página de la tabla y la referencia posicional miente.
                : `Para pre-ofertar necesitás que ${destacado.cabaña?.razon_social || 'la cabaña'} te autorice. Buscá el remate en la lista y tocá "Solicitar acceso".`
          }
        />
      )}

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_evento"
        dataSource={remates}
        columns={columnas}
        loading={loading}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: verFinalizados ? 'No hay remates' : 'No hay remates vigentes por ahora' }}
        rowClassName={(r) => (destacado && r.id_evento === destacado.id_evento
          ? 'remate-destacado' : '')}
      />
    </div>
  );
};

export default Remates;
